/**
 * Authentication API service
 * Handles login, signup, and authentication-related API calls
 */

import { apiClient } from './client';
import {
  AuthRequest,
  AuthResponse,
  AuthTokens,
  ShopCreateRequest,
  Shop,
  CreateUserRequest,
  ResetPasswordRequest
} from '../types';
import { getConfig, createLogger } from '../config/environment';

const logger = createLogger('AuthService');
const config = getConfig();

/**
 * Decode JWT token to extract payload
 * Simple base64 decoding without verification (verification happens on backend)
 */
function decodeJWT(token: string): any {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      logger.warn('Invalid JWT format');
      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    logger.error('Failed to decode JWT', error);
    return null;
  }
}

/**
 * Extract user role from JWT token
 */
function extractUserRole(token: string): 'ADMIN' | 'STAFF' | 'SUPER_ADMIN' | 'PARTNER' | undefined {
  const payload = decodeJWT(token);
  if (!payload) {
    return undefined;
  }

  // Check for role in payload (adjust the property name based on your JWT structure)
  const role = payload.role || payload.userRole || payload.authorities?.[0];

  console.log('🔍 [Auth] Extracted role from JWT:', role, 'from payload:', payload);

  if (role === 'SUPER ADMIN' || role === 'SUPER_ADMIN') {
    return 'SUPER_ADMIN';
  } else if (role === 'ADMIN') {
    return 'ADMIN';
  } else if (role === 'STAFF') {
    return 'STAFF';
  } else if (role === 'PARTNER') {
    return 'PARTNER';
  }

  return undefined;
}

export interface OtpGenerateRequest {
  countryCode: string;
  mobileNumber: string;
  captchaToken?: string;
  otpFor?: string;
}

export interface OtpGenerateResponse {
  success: boolean;
  message: string;
}

export interface OtpLoginRequest {
  authType: 'otp';
  countryCode: string;
  mobileNumber: string;
  otp: string;
}

export class AuthService {
  /**
   * Generate OTP for login
   * POST /api/otp/generate
   */
  async generateOtp(request: OtpGenerateRequest): Promise<OtpGenerateResponse> {
    try {
      logger.info('Generating OTP for login');
      const response = await apiClient.post<OtpGenerateResponse>('/api/otp/generate', request);
      const data = response.data;
      logger.info('OTP generate response', { success: data.success, message: data.message });
      return data;
    } catch (error: any) {
      logger.error('Failed to generate OTP', error);
      // Extract message from error response
      const message = error?.response?.data?.message || error?.message || 'Failed to send OTP';
      throw new Error(message);
    }
  }

  /**
   * Login with OTP
   * POST /api/auth/login with authType="otp"
   */
  async loginWithOtp(request: OtpLoginRequest): Promise<AuthTokens> {
    try {
      logger.info('Attempting OTP login');
      const response = await apiClient.post<AuthResponse>(config.authLoginEndpoint, request);

      if (response.status !== 200) {
        throw new Error('OTP login failed');
      }

      const data = response.data;

      if (data.status !== 'success') {
        throw new Error(data.message || 'OTP authentication failed');
      }

      const userRole = data.oauthToken ? extractUserRole(data.oauthToken) : undefined;

      const tokens: AuthTokens = {
        oauthToken: data.oauthToken,
        publicAccessToken: data.publicAccessToken,
        shopId: data.shopId,
        userId: data.userId,
        authLevel: 'OTP',
        expiresAt: Date.now() + (data.expiresInDays * 24 * 60 * 60 * 1000),
        userRole,
      };

      logger.info('OTP login successful', {
        userId: data.userId,
        shopId: data.shopId,
        userRole,
      });

      return tokens;
    } catch (error: any) {
      logger.error('OTP login failed', error);
      const message = error?.response?.data?.message || error?.message || 'OTP login failed';
      throw new Error(message);
    }
  }

  /**
   * Public access token (7 days, read-only)
   * GET /api/auth/login (empty body)
   */
  async getPublicAccessToken(): Promise<AuthTokens> {
    try {
      logger.info('Requesting public access token');
      const response = await apiClient.post<AuthResponse>(config.authLoginEndpoint, {});

      if (response.status !== 200) {
        throw new Error('Failed to get public access token');
      }

      const data = response.data;

      if (data.status !== 'success') {
        throw new Error(data.message || 'Authentication failed');
      }

      const tokens: AuthTokens = {
        publicAccessToken: data.publicAccessToken,
        shopId: data.shopId,
        userId: data.userId,
        authLevel: 'Public',
        expiresAt: Date.now() + (data.publicTokenExpiresInDays * 24 * 60 * 60 * 1000),
      };

      return tokens;
    } catch (error) {
      logger.error('Failed to get public access token', error);
      throw error;
    }
  }

  /**
   * Login with email or mobile number + password
   * POST /api/auth/login
   * Returns OAuth token (90 days, full CRUD access)
   * Login endpoint does not require any prior authentication
   */
  async login(request: AuthRequest): Promise<AuthTokens> {
    try {
      logger.info('Attempting login');

      // Login with credentials - no prior token required
      const response = await apiClient.post<AuthResponse>(config.authLoginEndpoint, request);

      if (response.status !== 200) {
        throw new Error('Login failed');
      }

      const data = response.data;

      if (data.status !== 'success') {
        throw new Error(data.message || 'Authentication failed');
      }

      // Extract user role from JWT token
      const userRole = data.oauthToken ? extractUserRole(data.oauthToken) : undefined;

      const tokens: AuthTokens = {
        oauthToken: data.oauthToken,
        publicAccessToken: data.publicAccessToken,
        shopId: data.shopId,
        userId: data.userId,
        authLevel: 'Password',
        expiresAt: Date.now() + (data.expiresInDays * 24 * 60 * 60 * 1000),
        userRole, // Add user role extracted from JWT
      };

      logger.info('Login successful - tokens will be stored securely with expiry tracking', {
        userId: data.userId,
        shopId: data.shopId,
        userRole,
        expiresInDays: data.expiresInDays,
        expiresAt: new Date(tokens.expiresAt).toISOString(),
      });

      return tokens;
    } catch (error) {
      logger.error('Login failed', error);
      throw error;
    }
  }

  /**
   * Register new user (staff/admin)
   * POST /api/auth/register
   * Note: Shop creation is done via POST /api/shops
   */
  async register(request: CreateUserRequest): Promise<any> {
    try {
      logger.info('Registering new user');
      const response = await apiClient.post<any>(config.authRegisterEndpoint, request);

      if (response.status !== 201 && response.status !== 200) {
        throw new Error('Failed to register user');
      }

      const data = response.data;

      if (data.status !== 'success') {
        throw new Error(data.message || 'Registration failed');
      }

      logger.info('User registered successfully', { userId: data.userId });
      return data;
    } catch (error) {
      logger.error('Failed to register user', error);
      throw error;
    }
  }

  /**
   * Reset password for authenticated user
   * POST /api/auth/reset-password
   * Automatically gets public token first
   */
  async resetPassword(request: ResetPasswordRequest): Promise<any> {
    try {
      logger.info('Resetting password');

      // Step 1: Get public token first
      await this.getPublicAccessToken();
      logger.info('Got public token, now resetting password');

      // Step 2: Reset password
      const response = await apiClient.post<any>(config.authResetPasswordEndpoint, request);

      if (response.status !== 200) {
        throw new Error('Failed to reset password');
      }

      const data = response.data;

      if (data.status !== 'success') {
        throw new Error(data.message || 'Password reset failed');
      }

      logger.info('Password reset successful');
      return data;
    } catch (error) {
      logger.error('Failed to reset password', error);
      throw error;
    }
  }

  /**
   * Create a new shop with optional admin user
   * POST /api/shops
   * Automatically gets public token first
   */
  async createShop(request: ShopCreateRequest): Promise<Shop> {
    try {
      logger.info('Creating new shop');

      // Step 1: Get public token first
      await this.getPublicAccessToken();
      logger.info('Got public token, now creating shop');

      // Step 2: Create shop
      const response = await apiClient.post<Shop>(config.shopsEndpoint, request);

      if (response.status !== 201 && response.status !== 200) {
        throw new Error('Failed to create shop');
      }

      logger.info('Shop created successfully', response.data.id);
      return response.data;
    } catch (error: any) {
      logger.error('Failed to create shop', error);

      // Extract meaningful error message from response
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        // If backend returns structured error with details
        if (errorData.details) {
          throw new Error(errorData.details);
        }

        // If backend returns message
        if (errorData.message) {
          throw new Error(errorData.message);
        }

        // If error is a string
        if (typeof errorData === 'string') {
          throw new Error(errorData);
        }
      }

      // Fallback to original error
      throw error;
    }
  }

  /**
   * Verify if token is still valid
   */
  async verifyToken(): Promise<boolean> {
    try {
      const tokens = await apiClient.getTokens();
      if (!tokens) {
        return false;
      }

      // Check if token is expired
      if (tokens.expiresAt && tokens.expiresAt < Date.now()) {
        logger.warn('Token expired');
        return false;
      }

      logger.info('Token is valid');
      return true;
    } catch (error) {
      logger.error('Token verification failed', error);
      return false;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const tokens = await apiClient.getTokens();
      if (!tokens || !tokens.oauthToken) {
        return false;
      }

      // Token exists and is not expired
      return !tokens.expiresAt || tokens.expiresAt > Date.now();
    } catch {
      return false;
    }
  }

  /**
   * Logout - clear stored tokens
   */
  async logout(): Promise<void> {
    try {
      logger.info('Logging out');
      await apiClient.clearTokens();
      logger.info('Logout successful');
    } catch (error) {
      logger.error('Logout failed', error);
      throw error;
    }
  }
}

// Create singleton instance
export const authService = new AuthService();

